import { ColumnDef, useReactTable } from '@tanstack/react-table';
import { DragDropContext, OnDragEndResponder } from 'react-beautiful-dnd';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { THead, TableComponent, TableContainer } from './common';
import { TableProps } from './types';
import { useTableStateManagement } from './useTableStateManagement';
import reorderColumn from './reorderColumn';
import TableHeaders from './Header';
import Body from './Body';

export type Column<T> = ColumnDef<T, T> & { id: string };

export default function Table<T>({
  data,
  styles,
  columns,
  tableContainerRef,
  settings = {},
  getRowTestId,
  fetchMoreOnBottomReached,
  overScanRowCount = 20,
  enableSortingRemoval,
  ...rest
}: TableProps<T>) {
  const { containerCss, tBodyCss, tHeadCss, tableCss, thRowCss, trCss } =
    styles ?? {};

  const {
    sorting,
    ordering,
    isExpandable = true,
    isVirtualized = true,
    sortSide = 'right',
  } = settings;
  const [order, setOrder] = ordering ?? [];
  const [sort, _setSort] = sorting ?? [];
  const tableStateManagement = useTableStateManagement<T>(settings);
  const table = useReactTable<T>({
    data,
    columns,
    enableSortingRemoval,
    ...tableStateManagement,
  });
  const isMobile = useIsMobile();

  const onDragEnd: OnDragEndResponder = (dropResult) => {
    if (dropResult.destination == null || order == null || setOrder == null) {
      return;
    }
    const newColumnOrder = reorderColumn(
      dropResult.draggableId,
      dropResult.destination.droppableId,
      order,
    );

    setOrder(newColumnOrder);
  };

  return (
    <TableContainer
      {...rest}
      onScroll={({ target }) => fetchMoreOnBottomReached?.(target)}
      ref={tableContainerRef}
      css={containerCss}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <TableComponent css={tableCss}>
          <THead css={tHeadCss}>
            <TableHeaders<T>
              sortSide={sortSide}
              sort={sort}
              table={table}
              order={order!}
              css={thRowCss}
              setOrder={setOrder!}
            />
          </THead>
          <Body<T>
            table={table}
            css={tBodyCss}
            trCss={trCss}
            isMobile={isMobile}
            isExpandable={isExpandable}
            getRowTestId={getRowTestId}
            isVirtualized={isVirtualized}
            overScanRowCount={overScanRowCount}
            tableContainerRef={tableContainerRef}
          />
        </TableComponent>
      </DragDropContext>
    </TableContainer>
  );
}
